import * as PIXI from "pixi.js";
import { getLabelTexture, warmLabelTextures } from "../Shared/textLabelCache";
import { FONT_STYLES, scaleStyle } from "../../fontStyles";

/**
 * The Hold & Win coin number — one Sprite, fed from textLabelCache.
 *
 * Why not a PIXI.Text (what the cells used before):
 *   - a Text rasterises at PIXI.settings.RESOLUTION, which is 1, while the
 *     renderer runs at up to 2x — so every number was drawn at half density
 *     and magnified, i.e. soft;
 *   - every new value re-rasterised a canvas and uploaded a texture, on the
 *     frame a spinning coin wrapped.
 * The cache draws each label ONCE, at the renderer's density, and hands out
 * the texture; the same value on forty cells is one texture.
 *
 * Never rounded: the number rides spines and landings, and snapping it to
 * whole pixels made it jitter.
 */

const STYLE_KEY = "hnw_coin_value";

/** An empty coin-number sprite, centred. */
export const createCoinLabel = () => {
  const sprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
  sprite.anchor.set(0.5);
  sprite.roundPixels = false;
  sprite.alpha = 0;
  sprite._label = "";
  sprite._fontSize = 0;
  sprite._labelWidth = 0;
  return sprite;
};

/**
 * Show `label` at `fontSize`, squeezed horizontally to `maxWidth` if wider.
 * An empty label hides the sprite. Re-textures only when the label or the size
 * actually changed.
 */
export const setCoinLabel = (
  sprite,
  label,
  fontSize,
  maxWidth = 0,
  // A FONT_STYLES key — a jackpot coin's revealed number uses its tier's
  // (coinValueMini … coinValueGrand). Every plain coin stays on coinValue.
  styleName = "coinValue",
) => {
  if (!sprite) return;
  if (!label) {
    // EMPTY texture, not just alpha 0: a label worn as a spine slot object
    // (a landed coin's number) has its alpha rewritten from the slot EVERY
    // frame by spine-pixi, so alpha alone never hid it — the End Collector's
    // coins kept their numbers after the value flew off.
    sprite.alpha = 0;
    sprite.texture = PIXI.Texture.EMPTY;
    sprite._label = "";
    sprite._fontSize = 0;
    sprite._labelWidth = 0;
    sprite._styleName = null;
    return;
  }
  const size = Math.round(fontSize);
  const style = FONT_STYLES[styleName] ? styleName : "coinValue";
  if (
    sprite._label !== label ||
    sprite._fontSize !== size ||
    (sprite._styleName ?? "coinValue") !== style
  ) {
    const entry = getLabelTexture(
      label,
      scaleStyle(FONT_STYLES[style], size),
      style === "coinValue" ? STYLE_KEY : `hnw_${style}`,
      size,
    );
    sprite.texture = entry?.texture || PIXI.Texture.EMPTY;
    sprite._label = label;
    sprite._fontSize = size;
    sprite._labelWidth = entry?.width || 0;
    sprite._styleName = style;
  }
  sprite.scale.set(1);
  if (maxWidth > 0 && sprite._labelWidth > maxWidth) {
    sprite.scale.x = maxWidth / sprite._labelWidth;
  }
  sprite.alpha = 1;
};

/**
 * Draw `labels` into the cache NOW, at each of `fontSizes` — so a coin that
 * later shows one of them only swaps a texture, and nothing is rasterised
 * while the reels move. Same style, key and rounding as setCoinLabel, which is
 * what makes the pre-drawn texture the one it finds.
 */
export const warmCoinLabels = (labels, fontSizes) => {
  if (!Array.isArray(labels) || !labels.length) return;
  for (const fontSize of fontSizes || []) {
    const size = Math.round(fontSize);
    if (!(size > 0)) continue;
    const style = scaleStyle(FONT_STYLES.coinValue, size);
    warmLabelTextures(
      labels.map((label) => ({ label, style, styleKey: STYLE_KEY, fontSize: size })),
    );
  }
};
